import { Form, Select } from 'antd';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getFormItemLabel } from '@/components/form-fields/utils';
import { SEARCH_DEBOUNCE_MS, useDebouncedCallback } from '@/utils/debounce';
import { api } from '@/api';

const { Option } = Select;
const SEARCH_PAGE_SIZE = 30;

interface OptionItem {
  value: number;
  label: string;
}

function sortOptionsByLabel(items: OptionItem[]): OptionItem[] {
  return [...items].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

export const CooperativeSearchSelect = observer(
  ({
    name,
    rules,
    i18nLabel,
    readOnly,
    allowClear = true,
    mode,
    initialSelectedOptions = [],
    ...rest
  }: {
    name: string;
    rules?: any[];
    i18nLabel: string;
    readOnly?: boolean;
    allowClear?: boolean;
    mode?: 'multiple';
    initialSelectedOptions?: OptionItem[];
  }) => {
    const [options, setOptions] = useState<OptionItem[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchCooperatives = useCallback(
      (searchText: string) => {
        if ((initialSelectedOptions?.length ?? 0) === 0) setLoading(true);
        api.cooperatives
          .getAll({
            name: searchText || undefined,
            limit: SEARCH_PAGE_SIZE,
            skip: 0,
            sort: 'name:1',
          })
          .source.then(({ data }: { data: { items: cyrian.api.cooperatives.Cooperative[] } }) => {
            const next = (data?.items ?? []).map((c) => ({
              value: c.id,
              label: c.name ?? String(c.id),
            }));
            setOptions(sortOptionsByLabel(next));
          })
          .catch(() => setOptions([]))
          .finally(() => setLoading(false));
      },
      [initialSelectedOptions?.length],
    );

    useEffect(() => {
      fetchCooperatives('');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const debouncedFetch = useDebouncedCallback(fetchCooperatives, SEARCH_DEBOUNCE_MS);

    const onSearch = useCallback(
      (value: string) => {
        if (value === undefined || value === null) {
          setOptions([]);
          return;
        }
        const trimmed = String(value).trim();
        debouncedFetch(trimmed);
      },
      [debouncedFetch],
    );

    const mergedOptions = useMemo(() => {
      const byId = new Map<number, OptionItem>();
      (initialSelectedOptions ?? []).forEach((o) => {
        byId.set(o.value, o);
      });
      options.forEach((o) => {
        byId.set(o.value, o);
      });
      return sortOptionsByLabel(Array.from(byId.values()));
    }, [initialSelectedOptions, options]);

    const label = getFormItemLabel(i18nLabel, undefined);

    return (
      <Form.Item name={name} label={label} rules={rules}>
        <Select
          showSearch
          allowClear={allowClear}
          mode={mode}
          placeholder={label}
          filterOption={false}
          onSearch={onSearch}
          onFocus={() => options.length === 0 && fetchCooperatives('')}
          loading={loading}
          disabled={readOnly}
          {...rest}
        >
          {mergedOptions.map((opt) => (
            <Option key={opt.value} value={opt.value}>
              {opt.label}
            </Option>
          ))}
        </Select>
      </Form.Item>
    );
  },
);
